import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { 
  Plus, 
  Trash2, 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  RefreshCw,
  Bitcoin,
  Gem,
  BarChart3,
  Target,
  Upload,
  Sparkles,
  AlertTriangle,
  ChevronRight,
  Star,
  Skull,
  Zap,
  Activity,
  DollarSign,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import TradeTracker from "@/components/TradeTracker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const ASSET_TYPES = [
  { value: "crypto", label: "Cryptocurrency", category: "crypto" },
  { value: "metal", label: "Precious Metal", category: "commodities" },
  { value: "stock", label: "Stock", category: "stocks" },
  { value: "option", label: "Option", category: "options" },
];

const SECTOR_ICONS = {
  "Technology": "💻",
  "Financial": "🏦",
  "Healthcare": "🏥",
  "Consumer": "🛒",
  "Energy": "⚡",
  "Industrial": "🏭",
  "Utilities": "💡",
  "ETF": "📊",
  "Crypto": "₿",
  "Crypto-Related": "🔗",
  "Commodities": "💎",
  "Other": "📁"
};

export default function Portfolio() {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [activeSector, setActiveSector] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [logTradeDialogOpen, setLogTradeDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  
  const [formData, setFormData] = useState({
    symbol: "",
    name: "",
    asset_type: "stock",
    quantity: "",
    purchase_price: "",
    purchase_date: new Date().toISOString().split("T")[0],
    option_type: "",
    strike_price: "",
    expiry_date: "",
    notes: "",
  });

  const [logTradeForm, setLogTradeForm] = useState({
    shares: "",
    entry_price: "",
    trade_type: "BUY"
  });

  const fetchPortfolio = useCallback(async () => {
    try {
      const response = await api.get("/portfolio/supercharged");
      setPortfolio(response.data);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
      // Fallback to v2 endpoint
      try {
        const fallback = await api.get("/portfolio/v2?category=all");
        setPortfolio(fallback.data);
      } catch {
        toast.error("Failed to load portfolio");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();
    const interval = setInterval(fetchPortfolio, 60000); // Refresh every 60s
    return () => clearInterval(interval);
  }, [fetchPortfolio]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPortfolio();
    toast.success("Portfolio refreshed!");
  };

  const handleAddAsset = async () => {
    if (!formData.symbol || !formData.quantity || !formData.purchase_price) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const assetType = ASSET_TYPES.find(t => t.value === formData.asset_type);
      await api.post("/portfolio/v2", {
        ...formData,
        symbol: formData.symbol.toUpperCase(),
        quantity: parseFloat(formData.quantity),
        purchase_price: parseFloat(formData.purchase_price),
        category: assetType?.category || "stocks",
      });
      
      toast.success(`Added ${formData.symbol.toUpperCase()} to portfolio!`);
      setDialogOpen(false);
      setFormData({
        symbol: "", name: "", asset_type: "stock", quantity: "", purchase_price: "",
        purchase_date: new Date().toISOString().split("T")[0],
        option_type: "", strike_price: "", expiry_date: "", notes: "",
      });
      fetchPortfolio();
    } catch (error) {
      toast.error("Failed to add asset");
    }
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      await api.delete(`/portfolio/v2/${assetId}`);
      toast.success("Asset removed");
      fetchPortfolio();
    } catch (error) {
      toast.error("Failed to delete asset");
    }
  };

  const handleLogTrade = async () => {
    if (!logTradeForm.shares || !logTradeForm.entry_price) {
      toast.error("Please enter shares and price");
      return;
    }

    try {
      const tradeData = {
        symbol: selectedAlert.symbol,
        trade_type: logTradeForm.trade_type,  // User selects BUY or SELL
        shares: parseFloat(logTradeForm.shares),
        entry_price: parseFloat(logTradeForm.entry_price),
        suggestion_source: "AI",
        suggestion_type: selectedAlert.message,
        notes: `AI Alert: ${selectedAlert.message} | Urgency: ${selectedAlert.urgency}%`
      };

      await api.post("/trade-tracker/add", tradeData);
      toast.success(`✅ Logged ${tradeData.trade_type} trade for ${tradeData.symbol}!`);
      setLogTradeDialogOpen(false);
      setSelectedAlert(null);
      setLogTradeForm({ shares: "", entry_price: "", trade_type: "BUY" });
    } catch (error) {
      toast.error("Failed to log trade");
    }
  };

  const getRecommendationColor = (action) => {
    switch (action) {
      case "BUY MORE": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      case "SELL": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "TAKE PROFITS": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "REVIEW": return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getSectorAssets = () => {
    if (!portfolio?.by_sector) return [];
    if (activeSector === "all") {
      return portfolio.assets || [];
    }
    return portfolio.by_sector[activeSector]?.assets || [];
  };

  // Skeleton loader component
  const SkeletonCard = () => (
    <Card className="bg-gray-800/50 border-gray-700 animate-pulse">
      <CardContent className="p-4">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <div className="h-5 bg-gray-700 rounded w-16" />
            <div className="h-3 bg-gray-700 rounded w-24" />
          </div>
          <div className="space-y-2 text-right">
            <div className="h-5 bg-gray-700 rounded w-20 ml-auto" />
            <div className="h-3 bg-gray-700 rounded w-16 ml-auto" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6" data-testid="portfolio-loading">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="space-y-2">
            <div className="h-8 bg-gray-700 rounded w-48 animate-pulse" />
            <div className="h-4 bg-gray-700 rounded w-64 animate-pulse" />
          </div>
        </div>
        {/* Stats Skeleton */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-gray-800/50 border-gray-700 animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-gray-700 rounded w-20 mb-2" />
                <div className="h-7 bg-gray-700 rounded w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Holdings Skeleton */}
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="portfolio-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Wallet className="w-8 h-8 text-cyan-400" />
            Supercharged Portfolio
            {portfolio?.whale_powered && <span title="Whale Powered">🐋</span>}
            {portfolio?.polygon_powered && <span title="Polygon Real-time">📊</span>}
          </h1>
          <p className="text-muted-foreground mt-1">
            Real-time prices • Whale signals • Smart recommendations
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-600 hover:bg-cyan-700 gap-2">
                <Plus className="w-4 h-4" />
                Add Asset
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-900 border-gray-700">
              <DialogHeader>
                <DialogTitle>Add New Asset</DialogTitle>
                <DialogDescription>Add a stock, crypto, or commodity to your portfolio</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Symbol *</Label>
                    <Input 
                      placeholder="AAPL" 
                      value={formData.symbol}
                      onChange={(e) => setFormData({...formData, symbol: e.target.value.toUpperCase()})}
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  <div>
                    <Label>Asset Type *</Label>
                    <Select value={formData.asset_type} onValueChange={(v) => setFormData({...formData, asset_type: v})}>
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {ASSET_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Name</Label>
                  <Input 
                    placeholder="Apple Inc." 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Quantity *</Label>
                    <Input 
                      type="number" 
                      placeholder="10"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  <div>
                    <Label>Purchase Price *</Label>
                    <Input 
                      type="number" 
                      placeholder="150.00"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea 
                    placeholder="Any notes about this position..."
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                <Button onClick={handleAddAsset} className="bg-cyan-600 hover:bg-cyan-700">
                  Add to Portfolio
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Urgent Alerts - BEG ME Feature */}
      {portfolio?.urgent_alerts?.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-bold flex items-center gap-2 text-yellow-400">
            <Bell className="w-5 h-5 animate-pulse" />
            🙏 URGENT ALERTS - ACTION NEEDED!
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {portfolio.urgent_alerts.map((alert, idx) => (
              <Card 
                key={idx} 
                className={`${
                  alert.type === "BUY" ? "border-emerald-500 bg-emerald-500/10" :
                  alert.type === "SELL" ? "border-red-500 bg-red-500/10" :
                  "border-orange-500 bg-orange-500/10"
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-bold">{alert.symbol}</span>
                    <Badge className={`${
                      alert.type === "BUY" ? "bg-emerald-500" :
                      alert.type === "SELL" ? "bg-red-500" :
                      "bg-orange-500"
                    }`}>
                      {alert.urgency}% URGENT
                    </Badge>
                  </div>
                  <p className="text-sm font-medium mb-2">{alert.message}</p>
                  <div className="flex justify-between text-xs text-muted-foreground mb-3">
                    <span>Current: ${alert.current_price?.toFixed(2)}</span>
                    <span className={alert.profit_loss_pct >= 0 ? "text-emerald-400" : "text-red-400"}>
                      {alert.profit_loss_pct >= 0 ? "+" : ""}{alert.profit_loss_pct?.toFixed(1)}%
                    </span>
                  </div>
                  <Button
                    size="sm"
                    className={`w-full ${
                      alert.type === "BUY" ? "bg-emerald-600 hover:bg-emerald-700" :
                      alert.type === "SELL" ? "bg-red-600 hover:bg-red-700" :
                      "bg-orange-600 hover:bg-orange-700"
                    }`}
                    onClick={() => {
                      setSelectedAlert(alert);
                      // Set default trade type based on alert type
                      const defaultType = alert.type === "SELL" ? "SELL" : "BUY";
                      setLogTradeForm({
                        shares: "",
                        entry_price: alert.current_price ? alert.current_price.toFixed(2) : "",
                        trade_type: defaultType
                      });
                      setLogTradeDialogOpen(true);
                    }}
                    data-testid={`log-trade-${alert.symbol}`}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Log This Trade
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Log Trade Dialog */}
      <Dialog open={logTradeDialogOpen} onOpenChange={setLogTradeDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {logTradeForm.trade_type === "BUY" ? (
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
              Log Trade - {selectedAlert?.symbol}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Alert Info */}
            <div className={`p-3 rounded-lg ${
              selectedAlert?.type === "BUY" ? "bg-emerald-500/20 border border-emerald-500/50" :
              selectedAlert?.type === "SELL" ? "bg-red-500/20 border border-red-500/50" :
              "bg-orange-500/20 border border-orange-500/50"
            }`}>
              <p className="text-sm font-medium text-white">{selectedAlert?.message}</p>
              <p className="text-xs text-gray-400 mt-1">Urgency: {selectedAlert?.urgency}%</p>
            </div>

            {/* BUY/SELL Selector */}
            <div>
              <Label className="text-gray-400 mb-2 block">Trade Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setLogTradeForm({...logTradeForm, trade_type: "BUY"})}
                  className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                    logTradeForm.trade_type === "BUY" 
                      ? "bg-emerald-600 border-emerald-500 text-white" 
                      : "bg-gray-800 border-gray-700 text-gray-400 hover:border-emerald-500/50"
                  }`}
                >
                  <TrendingUp className="w-4 h-4" />
                  BUY
                </button>
                <button
                  onClick={() => setLogTradeForm({...logTradeForm, trade_type: "SELL"})}
                  className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                    logTradeForm.trade_type === "SELL" 
                      ? "bg-red-600 border-red-500 text-white" 
                      : "bg-gray-800 border-gray-700 text-gray-400 hover:border-red-500/50"
                  }`}
                >
                  <TrendingDown className="w-4 h-4" />
                  SELL
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400">Shares</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={logTradeForm.shares}
                  onChange={(e) => setLogTradeForm({...logTradeForm, shares: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              <div>
                <Label className="text-gray-400">Entry Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={selectedAlert?.current_price?.toFixed(2)}
                  value={logTradeForm.entry_price}
                  onChange={(e) => setLogTradeForm({...logTradeForm, entry_price: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
            </div>

            <div className="text-sm text-gray-400 bg-gray-800 p-3 rounded-lg">
              <p>💰 Total Investment: <span className="text-white font-bold">
                ${(
                  (Number(logTradeForm.shares) || 0) * 
                  (Number(logTradeForm.entry_price) || 0)
                ).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </span></p>
            </div>

            <Button 
              onClick={handleLogTrade} 
              className={`w-full ${
                logTradeForm.trade_type === "BUY" ? "bg-emerald-600 hover:bg-emerald-700" :
                "bg-red-600 hover:bg-red-700"
              }`}
            >
              <Target className="w-4 h-4 mr-2" />
              Log {logTradeForm.trade_type} Trade & Track Performance
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Star & Villain of the Day */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {portfolio?.star_of_day && (
          <Card className="border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-amber-500/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-yellow-500/20 rounded-full">
                <Star className="w-8 h-8 text-yellow-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-yellow-400 font-medium">⭐ STAR OF THE DAY</p>
                <p className="text-xl font-bold text-white">{portfolio.star_of_day.symbol}</p>
                <p className="text-sm text-muted-foreground">{portfolio.star_of_day.name}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-emerald-400">
                  +{portfolio.star_of_day.change_today?.toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground">{portfolio.star_of_day.reason}</p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {portfolio?.villain_of_day && (
          <Card className="border-red-500/50 bg-gradient-to-r from-red-500/10 to-rose-500/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-red-500/20 rounded-full">
                <Skull className="w-8 h-8 text-red-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-red-400 font-medium">😈 VILLAIN OF THE DAY</p>
                <p className="text-xl font-bold text-white">{portfolio.villain_of_day.symbol}</p>
                <p className="text-sm text-muted-foreground">{portfolio.villain_of_day.name}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-red-400">
                  {portfolio.villain_of_day.change_today?.toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground">{portfolio.villain_of_day.reason}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-dark">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-cyan-400" />
              <span className="text-sm text-muted-foreground">Total Value</span>
            </div>
            <p className="text-2xl font-bold text-white">
              ${portfolio?.total_value?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || "0.00"}
            </p>
          </CardContent>
        </Card>
        
        <Card className="card-dark">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-5 h-5 text-blue-400" />
              <span className="text-sm text-muted-foreground">Invested</span>
            </div>
            <p className="text-2xl font-bold text-white">
              ${portfolio?.total_invested?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || "0.00"}
            </p>
          </CardContent>
        </Card>
        
        <Card className={`card-dark ${portfolio?.total_profit_loss >= 0 ? 'border-emerald-500/30' : 'border-red-500/30'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              {portfolio?.total_profit_loss >= 0 ? 
                <TrendingUp className="w-5 h-5 text-emerald-400" /> :
                <TrendingDown className="w-5 h-5 text-red-400" />
              }
              <span className="text-sm text-muted-foreground">Total P/L</span>
            </div>
            <p className={`text-2xl font-bold ${portfolio?.total_profit_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {portfolio?.total_profit_loss >= 0 ? '+' : ''}${portfolio?.total_profit_loss?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || "0.00"}
            </p>
          </CardContent>
        </Card>
        
        <Card className={`card-dark ${portfolio?.profit_loss_percentage >= 0 ? 'border-emerald-500/30' : 'border-red-500/30'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-muted-foreground">Return</span>
            </div>
            <p className={`text-2xl font-bold ${portfolio?.profit_loss_percentage >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {portfolio?.profit_loss_percentage >= 0 ? '+' : ''}{portfolio?.profit_loss_percentage?.toFixed(2) || "0.00"}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card/50 grid grid-cols-3 w-full sm:w-auto sm:inline-flex">
          <TabsTrigger value="overview" className="gap-1 sm:gap-2 text-xs sm:text-sm">
            <PieChart className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">By </span>Sector
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-1 sm:gap-2 text-xs sm:text-sm">
            <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">All </span>Holdings
          </TabsTrigger>
          <TabsTrigger value="tracker" className="gap-1 sm:gap-2 text-xs sm:text-sm">
            <Target className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Trade </span>Tracker
          </TabsTrigger>
        </TabsList>

        {/* Trade Tracker Tab */}
        <TabsContent value="tracker">
          <TradeTracker />
        </TabsContent>

        {/* By Sector View */}
        <TabsContent value="overview" className="space-y-6">
          {portfolio?.by_sector && Object.entries(portfolio.by_sector).length > 0 ? (
            Object.entries(portfolio.by_sector).map(([sector, data]) => (
              <div key={sector} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <span>{SECTOR_ICONS[sector] || "📁"}</span>
                    {sector}
                    <Badge variant="outline">{data.count} holdings</Badge>
                  </h3>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Sector Value</p>
                    <p className="font-bold">${data.total_value?.toLocaleString()}</p>
                    <p className={`text-sm ${data.total_profit_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {data.total_profit_loss >= 0 ? '+' : ''}${data.total_profit_loss?.toLocaleString()}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.assets?.map((asset, idx) => (
                    <AssetCard key={idx} asset={asset} onDelete={handleDeleteAsset} getRecommendationColor={getRecommendationColor} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <Card className="card-dark">
              <CardContent className="p-8 text-center">
                <Wallet className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-muted-foreground">No holdings yet</p>
                <p className="text-sm text-gray-600 mt-2">Add your first asset to get started</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* All Holdings View */}
        <TabsContent value="all" className="space-y-4">
          {portfolio?.assets?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolio.assets.map((asset, idx) => (
                <AssetCard key={idx} asset={asset} onDelete={handleDeleteAsset} getRecommendationColor={getRecommendationColor} />
              ))}
            </div>
          ) : (
            <Card className="card-dark">
              <CardContent className="p-8 text-center">
                <Wallet className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-muted-foreground">No holdings yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Separate Asset Card Component
function AssetCard({ asset, onDelete, getRecommendationColor }) {
  const [showDetails, setShowDetails] = useState(false);
  
  return (
    <Card className={`card-dark hover:border-gray-600 transition-all ${
      asset.recommendation?.urgency >= 70 ? 'border-yellow-500/50 animate-pulse' : ''
    }`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-white">{asset.symbol}</span>
              {asset.whale?.has_data && <span title="Whale Data">🐋</span>}
              {asset.change_today > 2 && <span title="Hot">🔥</span>}
            </div>
            <p className="text-xs text-muted-foreground">{asset.name || asset.symbol}</p>
          </div>
          <Badge className={getRecommendationColor(asset.recommendation?.action)}>
            {asset.recommendation?.action || "HOLD"}
          </Badge>
        </div>
        
        {/* Price & P/L */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-gray-800/50 rounded p-2 text-center">
            <p className="text-xs text-muted-foreground">Current</p>
            <p className="font-bold text-white">${asset.current_price?.toFixed(2)}</p>
            <p className={`text-xs ${asset.change_today >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {asset.change_today >= 0 ? '+' : ''}{asset.change_today?.toFixed(1)}% today
            </p>
          </div>
          <div className={`rounded p-2 text-center ${asset.profit_loss >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
            <p className="text-xs text-muted-foreground">P/L</p>
            <p className={`font-bold ${asset.profit_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {asset.profit_loss >= 0 ? '+' : ''}${asset.profit_loss?.toFixed(2)}
            </p>
            <p className={`text-xs ${asset.profit_loss_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {asset.profit_loss_pct >= 0 ? '+' : ''}{asset.profit_loss_pct?.toFixed(1)}%
            </p>
          </div>
        </div>
        
        {/* Holdings Info */}
        <div className="flex justify-between text-xs text-muted-foreground mb-3">
          <span>{asset.quantity} shares @ ${asset.purchase_price?.toFixed(2)}</span>
          <span>Value: ${asset.current_value?.toFixed(2)}</span>
        </div>
        
        {/* Recommendation Reason */}
        {asset.recommendation?.reason && (
          <div className={`rounded p-2 mb-3 ${
            asset.recommendation.action === "BUY MORE" ? 'bg-emerald-500/10 border border-emerald-500/30' :
            asset.recommendation.action === "SELL" ? 'bg-red-500/10 border border-red-500/30' :
            asset.recommendation.action === "TAKE PROFITS" ? 'bg-yellow-500/10 border border-yellow-500/30' :
            'bg-gray-800/50'
          }`}>
            <p className="text-xs">{asset.recommendation.reason}</p>
          </div>
        )}
        
        {/* Technical & Whale Info (expandable) */}
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full text-xs"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? "Hide Details" : "Show Technicals & Whale Data"}
          <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
        </Button>
        
        {showDetails && (
          <div className="mt-3 pt-3 border-t border-gray-700 space-y-2">
            {/* Technicals */}
            {asset.technicals && (
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="text-center">
                  <p className="text-muted-foreground">RSI</p>
                  <p className={`font-bold ${
                    asset.technicals.rsi < 30 ? 'text-emerald-400' :
                    asset.technicals.rsi > 70 ? 'text-red-400' :
                    'text-white'
                  }`}>
                    {asset.technicals.rsi?.toFixed(0) || "N/A"}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground">SMA20</p>
                  <p className="font-bold text-white">${asset.technicals.sma_20?.toFixed(2) || "N/A"}</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground">Trend</p>
                  <p className={`font-bold ${
                    asset.technicals.trend === 'BULLISH' ? 'text-emerald-400' :
                    asset.technicals.trend === 'BEARISH' ? 'text-red-400' :
                    'text-gray-400'
                  }`}>
                    {asset.technicals.trend || "N/A"}
                  </p>
                </div>
              </div>
            )}
            
            {/* Whale Data */}
            {asset.whale?.has_data && (
              <div className="bg-cyan-500/10 rounded p-2">
                <p className="text-xs text-cyan-400 font-medium mb-1">🐋 Whale Activity</p>
                <div className="flex justify-between text-xs">
                  <span>Sentiment: <span className={`font-bold ${
                    asset.whale.sentiment?.includes('BULLISH') ? 'text-emerald-400' :
                    asset.whale.sentiment?.includes('BEARISH') ? 'text-red-400' :
                    'text-gray-400'
                  }`}>{asset.whale.sentiment}</span></span>
                  <span>Conviction: {asset.whale.conviction}%</span>
                </div>
                {(asset.whale.call_premium > 0 || asset.whale.put_premium > 0) && (
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-emerald-400">Calls: ${(asset.whale.call_premium / 1000000).toFixed(2)}M</span>
                    <span className="text-red-400">Puts: ${(asset.whale.put_premium / 1000000).toFixed(2)}M</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Delete Button */}
        <div className="flex justify-end mt-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={() => onDelete(asset.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
